import clsx from 'clsx'
import dynamic from 'next/dynamic'
import { FC, ReactNode, useEffect, useState } from 'react'
import LazyLoad from 'react-lazy-load'

import { getSectionIdFromHash } from '@/hooks/sectionHash'

import styles from './Home.module.scss'
import { AboutMain } from '@/sections/AboutMain'

const Partners = dynamic(
  () => import('@/sections/Partners').then(({ Partners }) => Partners),
  {
    ssr: false
  }
)

const Platform = dynamic(
  () => import('@/sections/Platform').then(({ Platform }) => Platform),
  {
    ssr: false
  }
)
const Unlimited = dynamic(
  () => import('@/sections/Unlimited').then(({ Unlimited }) => Unlimited),
  {
    ssr: false
  }
)
const Billing = dynamic(
  () => import('@/sections/Billing').then(({ Billing }) => Billing),
  {
    ssr: false
  }
)
const Services = dynamic(
  () => import('@/sections/Services').then(({ Services }) => Services),
  {
    ssr: false
  }
)

const Cpa = dynamic(() => import('@/sections/Cpa').then(({ Cpa }) => Cpa), {
  ssr: false
})

type HomeSectionProps = {
  id: string
  className: string
  eager: boolean
  children: ReactNode
}

const HomeSection: FC<HomeSectionProps> = ({
  id,
  className,
  eager,
  children
}) => (
  <div id={id}>
    {eager ? (
      // Те же классы, что ставит react-lazy-load после появления в viewport
      <div className={clsx('LazyLoad', 'is-visible', className)}>{children}</div>
    ) : (
      <LazyLoad className={className}>{children}</LazyLoad>
    )}
  </div>
)

const useEagerSectionIds = () => {
  const [eagerIds, setEagerIds] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const rememberHashSection = () => {
      const id = getSectionIdFromHash()

      if (!id) {
        return
      }

      setEagerIds(prev => {
        if (prev.has(id)) {
          return prev
        }

        const next = new Set(prev)
        next.add(id)

        return next
      })
    }

    // hash не приходит на SSR — читаем после mount (deep-link / клик)
    rememberHashSection()

    window.addEventListener('hashchange', rememberHashSection)

    return () => {
      window.removeEventListener('hashchange', rememberHashSection)
    }
  }, [])

  return eagerIds
}

export const Home: FC = () => {
  const eagerIds = useEagerSectionIds()

  return (
    <>
      <AboutMain className={styles['home__about-main']} />
      <LazyLoad className={styles.home__partners}>
        <Partners />
      </LazyLoad>
      <HomeSection
        id="add_networks"
        className={styles.home__platform}
        eager={eagerIds.has('add_networks')}
      >
        <Platform />
      </HomeSection>
      <HomeSection
        id="rent_ads_account"
        className={styles.home__unlimited}
        eager={eagerIds.has('rent_ads_account')}
      >
        <Unlimited />
      </HomeSection>
      <HomeSection
        id="deposit_methods"
        className={styles.home__billing}
        eager={eagerIds.has('deposit_methods')}
      >
        <Billing />
      </HomeSection>
      <HomeSection
        id="agency"
        className={styles.home__services}
        eager={eagerIds.has('agency')}
      >
        <Services />
      </HomeSection>
      <HomeSection
        id="cpa_network"
        className={styles.home__cpa}
        eager={eagerIds.has('cpa_network')}
      >
        <Cpa />
      </HomeSection>
    </>
  )
}
